import './moreSetting.scss';
import { omit } from 'lodash';
import { lang } from '@umu/ukit-lang';
import { isMobile } from '@umu/umu-ua';
import { useUpdateEffect } from 'ahooks';
import { Iconfont, Drawer, Popover, Tooltip } from '@umu/umu-design';
import { If } from 'common/src/components';
import { translateSettingState } from '../zustand';
import React, { useEffect, useState } from 'react';
import { updUserAiSubtitleSetupService } from "../service";
import MoreSettingPanel from '../moreSettingPanel/moreSettingPanel';
import { LabelAndValueOptions, TranslateSettingState } from "../interface";
import { emitCustomEvent as emitCustomEventDefault } from "../emitCustomEvent";

export type MoreSettingProps = {
    uControlBar: any;
    emitCustomEvent?: any;
};

function MoreSetting(props: MoreSettingProps) {
    const { uControlBar, emitCustomEvent = emitCustomEventDefault } = props;

    const [speed, setSpeed] = useState<LabelAndValueOptions>({
        label: '1X',
        value: '1'
    });

    const [aiSubtitleSwitchChecked, setAiSubtitleSwitchChecked] = useState(true);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [popoverVisible, setPopoverVisible] = useState(false);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [isShowDrawerCancel, setIsShowDrawerCancel] = useState(true);
    const [setting, setSetting] = translateSettingState((state: TranslateSettingState) => [
        state.setting,
        state.setSetting,
    ]);

    const handleClickMoreSettingIcon = () => {
        if (isMobile) {
            setDrawerVisible(true);
            return;
        }
        setPopoverVisible(!popoverVisible);
    };

    const handleDrawerClose = () => {
        setDrawerVisible(false);
    };

    const handleUpdUserAiSubtitleSetup = () => {
        if (uControlBar.config?.subtitleConfig?.isNotSaveTranslateSetting || !uControlBar?.config?.hasSubtitle) {
            return;
        }

        if (!Object.keys(setting).every(key => setting[key] != undefined)) {
            return;
        }

        const params: { [key: string]: any } = {
            setup: JSON.stringify(omit(setting, ['lang', 'trans_lang', 'apply_to_learner']))
        }

        params.session_id = uControlBar.config.sessionId;

        if (!uControlBar?.config?.subtitleConfig?.translateSettingShow?.isHiddenApplyToLearner) {
            params.apply_to_learner = setting.apply_to_learner;
        }

        updUserAiSubtitleSetupService(params);
    }

    const mergeDisplayOriginal = (setup_lang: string, display_original: any) => {
        const translateSetting = uControlBar?.config?.subtitleConfig?.translateSetting;

        if (isMobile) {
            return setting.display_original;
        }

        if (display_original != null) {
            return display_original;
        }

        return translateSetting?.trans_lang?.includes(setup_lang) && translateSetting.trans_lang?.includes(setting.setup_lang) ? setting.display_original : 0;
    }

    const addEventListenerChangeSetupLang = () => {
        uControlBar.wrapContainer.addEventListener('ai-subtitle-change-setup-lang', (e: any) => {
            const {
                detail: { setup_lang, display_original: display_original_prop },
            } = e;

            const translateSetting = uControlBar?.config?.subtitleConfig?.translateSetting;
            let display_original = mergeDisplayOriginal(setup_lang, display_original_prop)

            setSetting({
                ...setting,
                setup_lang,
                display_original,
                lang: translateSetting?.lang || '',
                trans_lang: translateSetting?.trans_lang || '',
            });

        });

        uControlBar?.wrapContainer.addEventListener('ai-subtitle-translate-change', (e: any) => {
            const {
                detail: { setup_lang },
            } = e;

            setSetting({
                ...setting,
                setup_lang,
            });
        });
    }

    const addEventListenerSelectSpeed = () => {
        // 监听 wrapContainer 自定义事件 u-select:speed
        uControlBar.wrapContainer.addEventListener('u-select:speed', (_e: any) => {
            setPopoverVisible(false);
            setDrawerVisible(false);
        });
    }

    const addEventListenerClickEmptySpace = () => {
        uControlBar.wrapContainer.addEventListener('u-change:bar', (e: any) => {
            const { detail: { isActive } } = e;

            if (!isActive) {
                setPopoverVisible(false);
            }
        });

        uControlBar.wrapContainer.addEventListener('u-click:emptySpace', (_e: any) => {
            setPopoverVisible(false);
        });

        uControlBar.wrapContainer.addEventListener('u-click:popover-emptySpace', (_e: any) => {
            setPopoverVisible(false);
        });

        uControlBar.wrapContainer.addEventListener('u-click:drawer-emptySpace', (_e: any) => {
            setPopoverVisible(false);
        });
    }

    const handleSettingDefault = () => {
        if (!uControlBar?.config?.subtitleConfig?.translateSetting || uControlBar?.config?.subtitleConfig?.isNotSaveTranslateSetting) {
            return;
        }

        const translageSetting = uControlBar?.config?.subtitleConfig?.translateSetting;

        setSetting({
            lang: translageSetting.lang,
            trans_lang: translageSetting.trans_lang,
            setup_lang: translageSetting.setup_lang,
            font_size: String(translageSetting.font_size),
            trans_pos: String(translageSetting.trans_pos),
            original_pos: String(translageSetting.original_pos),
            display_original: Number(translageSetting.display_original),
            apply_to_learner: Number(translageSetting?.apply_to_learner || '0'),
        });
    }

    const renderDrawerCancel = () => {
        return (
            <div className="more-setting-drawer-footer" onClick={handleDrawerClose}>
                {lang('取消')}
            </div>
        );
    };

    const addEventListenerFullScreen = () => {
        uControlBar.wrapContainer.addEventListener('u-click:fullScreen', (event: any) => {
            const { active } = event.detail;
            setIsFullScreen(!active);
            moveDrawerContainer(active);
        });
    }

    const moveDrawerContainer = (active: boolean) => {
        const playerEl = document.querySelector('.u-player.wrap-umu-subtitle-container')
        const drawerEl = document.querySelector('.umu-design-comp-drawer.more-setting-drawer')
        if (!playerEl || !drawerEl) {
            return;
        }
        if (active) {
            document.body.appendChild(drawerEl.parentNode as HTMLElement);
            return
        }
        playerEl.appendChild(drawerEl.parentNode as HTMLElement);
    }

    const drawerGetContainer = () => {
        if (isFullScreen) {
            return document.querySelector('.u-player.wrap-umu-subtitle-container') as HTMLElement;
        }
        return document.body;
    }

    const renderTrigger = () => {
        const isShowSpeedSetting = uControlBar?.config?.hasSpeed;
        const isShowSubtitleSetting = uControlBar?.config?.hasSubtitle;

        if (isShowSpeedSetting && !isShowSubtitleSetting) {
            return speed.label
        }

        return <Iconfont type="UprightMore" />
    }

    useEffect(() => {
        addEventListenerSelectSpeed();
        addEventListenerFullScreen();
        addEventListenerClickEmptySpace();
        addEventListenerChangeSetupLang();
    }, [setting]);

    useUpdateEffect(() => {
        if (!popoverVisible && !drawerVisible) {
            handleUpdUserAiSubtitleSetup();
        }
    }, [drawerVisible, popoverVisible]);

    useEffect(() => {
        handleSettingDefault();
    }, [uControlBar?.config?.subtitleConfig?.translateSetting])

    return (
        <div className="u-custom-more-setting">
            <If condition={isMobile}>
                <>
                    <div className="more-setting-icon" onClick={handleClickMoreSettingIcon}>
                        {renderTrigger()}
                    </div>
                    <Drawer
                        closable={false}
                        placement="bottom"
                        visible={drawerVisible}
                        destroyOnClose={true}
                        className="more-setting-drawer"
                        onClose={handleDrawerClose}
                        getContainer={drawerGetContainer}
                        footer={isShowDrawerCancel ? renderDrawerCancel() : null}
                    >
                        <MoreSettingPanel
                            speed={speed}
                            setting={setting}
                            setSpeed={setSpeed}
                            setSetting={setSetting}
                            visible={drawerVisible}
                            uControlBar={uControlBar}
                            emitCustomEvent={emitCustomEvent}
                            setIsShowDrawerCancel={setIsShowDrawerCancel}
                            aiSubtitleSwitchChecked={aiSubtitleSwitchChecked}
                            setAiSubtitleSwitchChecked={setAiSubtitleSwitchChecked}
                        />
                    </Drawer>
                </>
            </If>
            <If condition={!isMobile}>
                <Popover
                    hideArrow
                    trigger="click"
                    placement="top"
                    visible={popoverVisible}
                    destroyTooltipOnHide={true}
                    content={<MoreSettingPanel speed={speed} setting={setting} setSpeed={setSpeed}
                        setSetting={setSetting} visible={popoverVisible} uControlBar={uControlBar} emitCustomEvent={emitCustomEvent} aiSubtitleSwitchChecked={aiSubtitleSwitchChecked}
                        setAiSubtitleSwitchChecked={setAiSubtitleSwitchChecked} />}
                    overlayClassName="u-ai-subtitle-more-setting-popover"
                    getPopupContainer={(triggerNode: HTMLElement) => triggerNode}
                >
                    <div className="more-setting-icon" onClick={handleClickMoreSettingIcon}>
                        <Tooltip title={lang('设置')} placement="bottom">
                            {renderTrigger()}
                        </Tooltip>
                    </div>
                </Popover>
            </If>
        </div>
    );
}

export default MoreSetting;
